import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import EmailListItem from '../../components/portal/EmailListItem';
import EmailViewModal from '../../components/portal/EmailViewModal';
import EmailComposeModal from '../../components/portal/EmailComposeModal';
import EditProposalReplySettlePrompt from '../../components/portal/EditProposalReplySettlePrompt';
import { proposalToSettleAfterReply } from '../../utils/editProposalReplySettle';
import type { PortalEmailEditProposal } from '../../types/portal';
import InboxViewDropdowns from '../../components/portal/InboxViewDropdowns';
import type { InboxSortBy, InboxSortOrder } from '../../components/portal/InboxViewDropdowns';
import FilterTabs, { type TabFilter } from '../../components/portal/FilterTabs';
import InboxBulkBar from '../../components/portal/InboxBulkBar';
import TrashGuardModal from '../../components/portal/TrashGuardModal';
import Toast from '../../components/portal/Toast';
import { portalService } from '../../services/portal';
import type { PortalEmailItem } from '../../types/portal';
import { useToast } from '../../hooks/useToast';
import { mapEmailToComponent } from '../../utils/mapEmailToComponent';
import {
  parseTrashGuardError,
  partitionGuarded,
  type GuardedTrashEmail,
} from '../../utils/trashGuard';
import styles from './PortalInboxPage.module.css';

const PAGE_SIZE = 50;

const AllEmailsPage: React.FC = () => {
  const [emails, setEmails] = useState<PortalEmailItem[]>([]);
  const [totalEmails, setTotalEmails] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search);
  const [selectedEmailId, setSelectedEmailId] = useState<string | null>(null);
  const [showCompose, setShowCompose] = useState(false);
  const [composeAction, setComposeAction] = useState<'reply' | 'reply_all' | 'forward'>('reply');
  // After a reply on an email that carries an OPEN Merlin change card: ask
  // whether the reply settled it (kanban 006f1c9e). null = nothing to ask.
  const [replySettle, setReplySettle] = useState<{
    proposal: PortalEmailEditProposal;
    replyText: string;
  } | null>(null);
  const [activeTab, setActiveTab] = useState<TabFilter>('all');
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
  const [bulkTrashing, setBulkTrashing] = useState(false);
  // Server trash guard (409): guarded emails + the ids of the blocked request.
  const [trashGuard, setTrashGuard] = useState<{
    guarded: GuardedTrashEmail[];
    pendingIds: string[];
  } | null>(null);

  // Sort controls
  const [sortBy, setSortBy] = useState<InboxSortBy>('received_at');
  const [sortOrder, setSortOrder] = useState<InboxSortOrder>('desc');

  const { toasts, addToast, removeToast } = useToast();
  const loadedCountRef = useRef(0);

  // Fetch emails
  const fetchEmails = useCallback(
    async (loadMore = false) => {
      try {
        if (loadMore) {
          setLoadingMore(true);
        } else {
          setLoading(true);
          setError(null);
        }
        const offset = loadMore ? loadedCountRef.current : 0;
        const response = await portalService.listInboxEmails({
          view: 'all',
          limit: PAGE_SIZE,
          offset,
          search: debouncedSearch || undefined,
          sort_by: sortBy,
          sort_order: sortOrder,
          exclude_sent: true,
        });
        if (loadMore) {
          setEmails((prev) => {
            const next = [...prev, ...response.emails];
            loadedCountRef.current = next.length;
            return next;
          });
        } else {
          setEmails(response.emails);
          loadedCountRef.current = response.emails.length;
        }
        setTotalEmails(response.total);
      } catch (err) {
        console.error('Failed to fetch emails:', err);
        if (!loadMore) {
          setError('Failed to load emails. Please try again.');
        }
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [debouncedSearch, sortBy, sortOrder],
  );

  useEffect(() => {
    fetchEmails(false);
  }, [fetchEmails]);

  // Clear selection when search changes
  useEffect(() => {
    setSelectedEmailId(null);
  }, [debouncedSearch]);

  const handleLoadMore = useCallback(() => {
    fetchEmails(true);
  }, [fetchEmails]);

  // Mark an email as read in local state + backend. The navbar badge reads
  // the cached inbox stats, so drop the cache: both inbox views share the
  // same row and the Orders Inbox must not keep showing a stale count.
  const markEmailAsRead = useCallback(
    async (emailId: string) => {
      setEmails((prev) =>
        prev.map((e) =>
          e.id === emailId ? { ...e, is_unread: false } : e,
        ),
      );
      portalService.invalidateStatsCache();
      portalService.toggleReadStatus(emailId, true).catch((err) => {
        console.error('Failed to mark email as read:', err);
      });
    },
    [],
  );

  // Mark an email as unread in local state + backend
  const markEmailAsUnread = useCallback(
    async (emailId: string) => {
      setEmails((prev) =>
        prev.map((e) =>
          e.id === emailId ? { ...e, is_unread: true } : e,
        ),
      );
      portalService.invalidateStatsCache();
      portalService.toggleReadStatus(emailId, false).catch((err) => {
        console.error('Failed to mark email as unread:', err);
      });
    },
    [],
  );

  // Opening an email marks it read, like a normal mail client (2026-10-15
  // revert of the 09-24 "never on open" rule, kanban a2888ff4 reverting
  // cda64d0f: the desk was clearing the same email here and in its personal
  // inbox). Mark as unread still sticks while the email stays open; only a
  // fresh selection re-marks.
  const handleEmailSelect = useCallback(
    (emailId: string) => {
      setSelectedEmailId(emailId);
      const email = emails.find((e) => e.id === emailId);
      if (email?.is_unread) {
        markEmailAsRead(emailId);
      }
    },
    [emails, markEmailAsRead],
  );

  const handleCheckboxChange = useCallback(
    (emailId: string, checked: boolean) => {
      setCheckedIds((prev) => {
        const next = new Set(prev);
        if (checked) {
          next.add(emailId);
        } else {
          next.delete(emailId);
        }
        return next;
      });
    },
    [],
  );

  const handleBulkMarkRead = useCallback(async () => {
    const ids = Array.from(checkedIds);
    setEmails((prev) =>
      prev.map((e) => (checkedIds.has(e.id) ? { ...e, is_unread: false } : e)),
    );
    setCheckedIds(new Set());
    await Promise.allSettled(
      ids.map((id) => portalService.toggleReadStatus(id, true)),
    );
  }, [checkedIds]);

  const handleBulkMarkUnread = useCallback(async () => {
    const ids = Array.from(checkedIds);
    setEmails((prev) =>
      prev.map((e) => (checkedIds.has(e.id) ? { ...e, is_unread: true } : e)),
    );
    setCheckedIds(new Set());
    await Promise.allSettled(
      ids.map((id) => portalService.toggleReadStatus(id, false)),
    );
  }, [checkedIds]);

  const performBulkTrash = useCallback(async (
    ids: string[],
    confirmGuarded: boolean,
  ) => {
    const idSet = new Set(ids);
    setBulkTrashing(true);
    let previousEmails: PortalEmailItem[] = [];
    setEmails((prev) => { previousEmails = prev; return prev; });
    try {
      setEmails((prev) => prev.filter((e) => !idSet.has(e.id)));
      setCheckedIds(new Set());
      await portalService.bulkMarkAsTrash(ids, { confirmGuarded });
      fetchEmails();
    } catch (err) {
      // Roll back the optimistic removal; on a trash-guard 409 show the
      // confirm dialog listing the guarded emails instead of a toast.
      setEmails(previousEmails);
      setCheckedIds(idSet);
      const guarded = parseTrashGuardError(err);
      if (guarded) {
        setTrashGuard({ guarded, pendingIds: ids });
      } else {
        addToast('Failed to trash selected emails. Please try again.', 'error');
      }
    } finally {
      setBulkTrashing(false);
    }
  }, [fetchEmails, addToast]);

  const handleBulkTrash = useCallback(() => {
    void performBulkTrash(Array.from(checkedIds), false);
  }, [checkedIds, performBulkTrash]);

  const closeEmailViewer = useCallback(() => {
    setSelectedEmailId(null);
  }, []);

  const selectedEmail = useMemo(
    () => emails.find((e) => e.id === selectedEmailId) ?? null,
    [emails, selectedEmailId],
  );
  const mappedSelected = selectedEmail ? mapEmailToComponent(selectedEmail) : null;

  // Client-side tab filtering (unread only)
  const filteredEmails = useMemo(() => {
    if (activeTab === 'unread') {
      return emails.filter((e) => e.is_unread);
    }
    return emails;
  }, [emails, activeTab]);

  // Tab definitions
  const tabs = useMemo(() => {
    const unreadCount = emails.filter((e) => e.is_unread).length;
    const result: { id: TabFilter; label: string; count: number; variant?: 'default' | 'urgent' }[] = [
      { id: 'all', label: 'All', count: totalEmails || emails.length },
      { id: 'unread', label: 'Unread', count: unreadCount },
    ];
    return result;
  }, [emails, totalEmails]);

  if (error) {
    return (
      <div className={styles.header}>
        <div className={styles.errorState}>
          <p>{error}</p>
          <button
            className={`${styles.btn} ${styles.btnPrimary}`}
            onClick={() => fetchEmails(false)}
            type="button"
            data-action="inbox_retry_load"
            data-outcome="request"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={styles.header} data-section="orders_inbox_header">
        <div className={styles.headerLeft}>
          <h1 className={styles.title}>Orders Inbox</h1>
          <InboxViewDropdowns
            sortBy={sortBy}
            onSortByChange={(val) => { setSortBy(val); setSelectedEmailId(null); }}
            sortOrder={sortOrder}
            onSortOrderChange={(val) => { setSortOrder(val); setSelectedEmailId(null); }}
          />
        </div>
        <div className={styles.actions}>
          <button
            className={`${styles.btn} ${styles.btnOutline}`}
            onClick={() => fetchEmails(false)}
            type="button"
            data-action="inbox_refresh"
            data-outcome="request"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <polyline points="23 4 23 10 17 10"></polyline>
              <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
            </svg>
            Refresh
          </button>
        </div>
      </div>

      {/* Search bar */}
      <div className={styles.searchBar} data-section="inbox_search">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <input
          className={styles.searchInput}
          type="text"
          role="searchbox"
          aria-label="Search emails"
          placeholder="Search by subject, sender, customer, or content..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-action="inbox_search"
          data-record-value="query"
        />
        {search && (
          <button
            className={styles.searchClear}
            onClick={() => setSearch('')}
            type="button"
            title="Clear search"
            data-action="inbox_search_clear"
          >
            &times;
          </button>
        )}
      </div>

      <FilterTabs
        activeTab={activeTab}
        tabs={tabs}
        onTabChange={(tab) => {
          setActiveTab(tab);
        }}
      />

      {checkedIds.size > 0 && (
        <InboxBulkBar
          count={checkedIds.size}
          onMarkRead={handleBulkMarkRead}
          onMarkUnread={handleBulkMarkUnread}
          onTrash={handleBulkTrash}
          trashing={bulkTrashing}
          onClear={() => setCheckedIds(new Set())}
        />
      )}

      <div className={styles.listContainer} data-section="email_list">
        <div className={styles.list}>
          {loading ? (
            <div className={styles.emptyState}>
              <p>Loading emails...</p>
            </div>
          ) : filteredEmails.length === 0 ? (
            <div className={styles.emptyState}>
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
              </svg>
              <p>{debouncedSearch ? 'No emails match your search' : 'No received emails found'}</p>
            </div>
          ) : (
            <>
              {filteredEmails.map((email) => {
                const mapped = mapEmailToComponent(email);
                return (
                  <EmailListItem
                    key={email.id}
                    email={mapped}
                    isSelected={selectedEmailId === email.id}
                    isChecked={checkedIds.has(email.id)}
                    onClick={() => handleEmailSelect(email.id)}
                    onCheckboxChange={(checked) =>
                      handleCheckboxChange(email.id, checked)
                    }
                  />
                );
              })}
              {/* Pagination footer */}
              <div className={styles.paginationFooter} data-section="pagination">
                <span className={styles.paginationCount}>
                  Showing {emails.length} of {totalEmails.toLocaleString()}
                </span>
                {emails.length < totalEmails && (
                  <button
                    className={`${styles.btn} ${styles.btnOutline} ${styles.loadMoreBtn}`}
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    type="button"
                    data-action="inbox_load_more"
                    data-outcome="request"
                  >
                    {loadingMore
                      ? 'Loading...'
                      : `Load More (${Math.min(PAGE_SIZE, totalEmails - emails.length)} more)`}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {selectedEmailId && mappedSelected && (
        <EmailViewModal
          email={mappedSelected}
          emailId={selectedEmailId}
          onClose={closeEmailViewer}
          onOrderAction={(action, orderId) => {
            addToast(
              `Order ${orderId.slice(0, 8)}... ${action === 'submit' ? 'submitted to Sage' : action === 'cancel' ? 'cancelled' : action}`,
              action === 'cancel' ? 'warning' : 'success',
            );
          }}
          onReplyClick={(action) => {
            setComposeAction(action);
            setShowCompose(true);
          }}
          onEmailChanged={() => {
            fetchEmails();
          }}
          onMarkUnread={markEmailAsUnread}
          onMarkRead={markEmailAsRead}
          isUnread={!!mappedSelected.isUnread}
          onToast={addToast}
        />
      )}

      {showCompose && (
        <EmailComposeModal
          emailId={selectedEmailId}
          action={composeAction}
          onClose={() => setShowCompose(false)}
          onSent={(isSandbox, sent) => {
            addToast(
              isSandbox
                ? 'Sandbox: email logged (not sent)'
                : 'Email sent successfully!',
              isSandbox ? 'warning' : 'success',
            );
            // Replying is the other thing (besides Mark as read) that clears unread.
            if (selectedEmailId) markEmailAsRead(selectedEmailId);
            fetchEmails(false);
            // The desk answered the customer; is the open Merlin card settled?
            // (11 of 13 aging flags were desk-answered threads — AU02393.)
            const replied = emails.find((e) => e.id === selectedEmailId);
            const proposal = sent ? proposalToSettleAfterReply(replied, sent.action, sent.toAddresses) : null;
            if (proposal) setReplySettle({ proposal, replyText: sent?.bodyText ?? '' });
          }}
        />
      )}

      {replySettle && (
        <EditProposalReplySettlePrompt
          proposal={replySettle.proposal}
          replyText={replySettle.replyText}
          onDismissed={() => {
            setReplySettle(null);
            addToast(
              `Merlin change card on ${replySettle.proposal.sage_order_no ?? 'the order'} dismissed`,
              'success',
            );
            fetchEmails(false);
          }}
          onKeepOpen={() => setReplySettle(null)}
        />
      )}

      {trashGuard && (
        <TrashGuardModal
          guarded={trashGuard.guarded}
          totalSelected={trashGuard.pendingIds.length}
          busy={bulkTrashing}
          onCancel={() => setTrashGuard(null)}
          onTrashAnyway={() => {
            const ids = trashGuard.pendingIds;
            setTrashGuard(null);
            void performBulkTrash(ids, true);
          }}
          onSkipGuarded={() => {
            const { unguardedIds } = partitionGuarded(
              trashGuard.pendingIds,
              trashGuard.guarded,
            );
            setTrashGuard(null);
            if (unguardedIds.length > 0) {
              void performBulkTrash(unguardedIds, false);
            }
          }}
        />
      )}

      <Toast toasts={toasts} onRemove={removeToast} />
    </>
  );
};

export default AllEmailsPage;
